import React, {useEffect} from "react";
import {useBuildingKey, getAllUniqueRiskTypesFromBuilding} from "../../Helpers/utils";
import ComponentSection from "../../Components/ComponentSection";
import {Helmet} from "react-helmet-async";
import {BreadcrumbComponent} from "../../Components/BreadCrumb";
import {BaseType, CardinalDirection} from "../../types/Enums";
import {translate} from "../../types/EnumTranslations";
import InfoPanel, {InfoItem} from "../../Components/InfoPanel";
import ClickableImage from "../../Components/ClickableImage";
import compass from "../../Images/compass.png";
import {BaseComponentImpl} from "../../types/implementations";
import {useBuilding} from "../../Helpers/BuildingContext";

interface GenericComponentPageProps {
    type: BaseType;
}

const GenericComponentListPage: React.FC<GenericComponentPageProps> = ({type}) => {
    const pageTitle = translate(type);
    const sortByDirection = type === BaseType.Roof || type === BaseType.Facade;
    const buildingKey = useBuildingKey();
    const { currentBuilding, buildingError, loadingBuilding, fetchBuilding } = useBuilding();

    useEffect(() => {
        const loadBuildingData = async () => {
            if (buildingKey) {
                await fetchBuilding(buildingKey);
            }
        };
        loadBuildingData().then();
    }, [buildingKey, fetchBuilding]);

    if (loadingBuilding) return <p></p>;
    if (buildingError) return <p style={{ color: "red" }}>{buildingError}</p>;
    if (!buildingKey) return <p>Missing building key</p>;
    if (!currentBuilding) return <p>Building data missing</p>

    const { model, summary } = currentBuilding;

    const components = model && Object.values(
        model.structure?.components ?? []
        ).filter((component) => component.type === type)
        .map((item) => new BaseComponentImpl(item));

    // Get risk constructions for this specific component type
    const riskConstructions = components ? getAllUniqueRiskTypesFromBuilding({
        model: {
            structure: {
                components: components.reduce((acc, component) => {
                    acc[component.key] = component;
                    return acc;
                }, {} as { [key: string]: any })
            }
        }
    }) : [];

    const infoItems = new Array<InfoItem>()
    infoItems.push({label: "Typ", value: translate(type)})
    infoItems.push({label: "Antal", value: components?.length ?? 0})
    
    let totalArea = components?.reduce((sum, component) => {
        const totalArea = component.totalArea(currentBuilding);
        if (totalArea != null) {
            return sum + totalArea;
        }
        return sum;
    }, 0);
    
    infoItems.push({label: "Total area", value: `${totalArea?.toFixed(1) ?? 0} kvm`})
    
    // Check if any component has windows attached
    let windowCount = 0;
    let totalWindowArea = 0;
    
    if (currentBuilding?.model?.structure?.components && components) {
        components.forEach(component => {
            // Count windows for this component
            Object.values(currentBuilding.model.structure?.components || {}).forEach(comp => {
                if (comp.type === BaseType.Window) {
                    const parentSurfaces = [
                        (comp as any).parentSurface?.value,
                        (comp as any).parentSurface1?.value,
                        (comp as any).parentSurface2?.value
                    ].filter(Boolean);
                    
                    if (parentSurfaces.includes(component.key)) {
                        windowCount++;
                        const windowImpl = new BaseComponentImpl(comp);
                        const windowArea = windowImpl.totalArea(currentBuilding);
                        if (windowArea) {
                            totalWindowArea += windowArea;
                        }
                    }
                }
            });
        });
    }
    
    if (windowCount > 0) {
        infoItems.push({label: "Fönster", value: `${windowCount} st`})
    }
    
    if (windowCount > 0 && totalArea) {
        const areaExcludingWindows = totalArea - totalWindowArea;
        infoItems.push({label: "Area exkl fönster", value: `${areaExcludingWindows.toFixed(1)} kvm`})
    }
    
    // Check if any component has child components attached (only for Roof and Facade)
    let sheetMetalCount = 0;
    let chimneyCount = 0;
    let drainageCount = 0;
    
    if ((type === BaseType.Roof || type === BaseType.Facade) && currentBuilding?.model?.structure?.components && components) {
        components.forEach(component => {
            // Count child components for this component
            Object.values(currentBuilding.model.structure?.components || {}).forEach(comp => {
                const parentSurface = (comp as any).parentSurface?.value;
                
                if (parentSurface === component.key) {
                    if (comp.type === BaseType.SheetMetal) {
                        sheetMetalCount++;
                    } else if (comp.type === BaseType.Chimney) {
                        chimneyCount++;
                    } else if (comp.type === BaseType.RoofDrainage) {
                        drainageCount++;
                    }
                }
            });
        });
    }
    
    if (sheetMetalCount > 0) {
        infoItems.push({label: translate(BaseType.SheetMetal), value: `${sheetMetalCount} st`})
    }
    
    if (chimneyCount > 0) {
        infoItems.push({label: translate(BaseType.Chimney), value: `${chimneyCount} st`})
    }
    
    if (drainageCount > 0) {
        infoItems.push({label: translate(BaseType.RoofDrainage), value: `${drainageCount} st`})
    }
    
    // Add risk construction information if any exist
    if (riskConstructions.length > 0) {
        const riskTypes = riskConstructions.map(risk => risk.riskType).join(', ');
        infoItems.push({label: "Riskkonstruktioner", value: riskTypes})
    }

    return (
        <>
            <BreadcrumbComponent
                items={[
                    {text: summary.name ?? 'Byggnad', link: `/building/${buildingKey}`},
                    {text: pageTitle}
                ]}
            />
            <div style={{
                margin: '20px',
                padding: "40px",
                backgroundColor: "white",
                borderRadius: '10px',
                maxWidth: '1500px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}>
                <Helmet>
                    <title>{`${pageTitle}: ${summary.name}`}</title>
                </Helmet>

                <div style={{display: 'flex', gap: '40px'}}>
                {components ?
                    sortByDirection ?
                        <div style={{flex: '1', display: 'flex', flexDirection: 'column', gap: '40px'}}>
                            {Object.values(CardinalDirection).map((direction) => {
                                const filtered = components.filter(
                                    (item) => item.cardinalDirection?.value === direction
                                );

                                return filtered.length > 0 ? (
                                    <ComponentSection
                                        key={direction}
                                        title={translate(direction)}
                                        components={filtered}
                                        building={currentBuilding}
                                        buildingKey={buildingKey}
                                        icon={`types/${filtered[0].type}`}
                                    />
                                ) : null;
                            })}

                            {(() => {
                                const itemsWithoutDirection = components.filter(
                                    (item) => item.cardinalDirection?.value === undefined
                                );

                                return itemsWithoutDirection.length > 0 ? (
                                    <div>
                                        <ComponentSection
                                            key={""}
                                            title={"Utan väderstreck"}
                                            components={itemsWithoutDirection}
                                            building={currentBuilding}
                                            buildingKey={buildingKey}
                                            icon={`types/${itemsWithoutDirection[0].type}`}
                                        />
                                    </div>
                                ) : null;
                            })()}
                        </div>
                        :
                        <div style={{display: 'flex', flexDirection: 'column', gap: '40px'}}>
                            <ComponentSection
                                key={type}
                                title={pageTitle}
                                components={components}
                                building={currentBuilding}
                                buildingKey={buildingKey}
                                icon={`types/${type}`}
                            />
                        </div>
                    :
                    (
                        <p>Inga byggdelar</p>
                    )}
                    {
                        sortByDirection &&
                        <div style={{ width: 'fit-content'}}>
                            <h3>&nbsp;</h3>
                            {
                                model.mapImages &&
                                <div style={{
                                    width: '150px',
                                    height: '150px',
                                    position: 'relative'
                                }}>
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%'
                                    }}>
                                        <ClickableImage
                                            buildingKey={model.key}
                                            photo={model.mapImages[model.currentMapImageKey ?? ""]}
                                            size={150}
                                        />
                                    </div>
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        zIndex: 1,
                                        pointerEvents: 'none'
                                    }}>
                                        <img
                                            src={compass}
                                            alt={"Compass"}
                                            style={{
                                                padding: '8px',
                                                width: '100%',
                                                height: '100%'
                                            }}
                                        />
                                    </div>
                                </div>
                            }
                        </div>
                    }
                    <div style={{flex: '1', maxWidth: '500px'}}>
                        <h3>Fakta</h3>
                        <InfoPanel items={infoItems} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default GenericComponentListPage;